import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { presupuestosApi } from '../lib/api';
import { formatCurrency, formatDate, formatDateTime, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Send, 
  Copy, 
  Trash2, 
  Eye,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

export default function DetallePresupuestoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [presupuesto, setPresupuesto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPresupuesto = async () => {
      try {
        const response = await presupuestosApi.getOne(id);
        setPresupuesto(response.data);
      } catch (error) {
        console.error('Error loading presupuesto:', error);
        toast.error('Error al cargar el presupuesto');
        navigate('/presupuestos');
      } finally {
        setLoading(false);
      }
    };
    loadPresupuesto();
  }, [id, navigate]);

  const handleEnviar = async () => {
    try {
      const response = await presupuestosApi.enviar(id);
      toast.success('Presupuesto enviado correctamente');
      setPresupuesto({ ...presupuesto, estado: 'pendiente', enviado_at: new Date().toISOString() });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    }
  };

  const handleDuplicar = async () => {
    try {
      const response = await presupuestosApi.duplicar(id);
      toast.success('Presupuesto duplicado');
      navigate(`/presupuestos/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar');
    }
  };

  const handleEliminar = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este presupuesto?')) return;
    
    try {
      await presupuestosApi.delete(id);
      toast.success('Presupuesto eliminado');
      navigate('/presupuestos');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getEventIcon = (tipo) => {
    switch (tipo) {
      case 'aprobado':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'rechazado':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'visto':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'enviado':
        return <Send className="w-4 h-4 text-violet-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!presupuesto) return null;

  const canEdit = hasPermission(['OWNER', 'MANAGER']);
  const publicUrl = `${window.location.origin}/p/${presupuesto.enlace_publico}`;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn" data-testid="detalle-presupuesto-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/presupuestos')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {presupuesto.numero}
                </h1>
                <span className={`px-3 py-1 text-sm font-medium rounded border ${getStatusColor(presupuesto.estado)}`}>
                  {getStatusLabel(presupuesto.estado)}
                </span>
              </div>
              <p className="text-slate-500 mt-1">
                Creado el {formatDate(presupuesto.created_at)}
              </p>
            </div>
          </div>
          
          {canEdit && (
            <div className="flex items-center gap-2">
              {presupuesto.estado === 'borrador' && (
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleEnviar}>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </Button>
              )}
              <Button variant="outline" onClick={handleDuplicar}>
                <Copy className="w-4 h-4 mr-2" />
                Duplicar
              </Button>
              <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={handleEliminar}>
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quote Preview */}
            <Card>
              <CardContent className="p-0">
                <div className="quote-preview border-0 shadow-none">
                  <div className="quote-header">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {presupuesto.empresa?.nombre_comercial || 'Tu Empresa'}
                        </h3>
                        {presupuesto.empresa?.direccion && (
                          <p className="text-slate-500 text-sm mt-1">{presupuesto.empresa.direccion}</p>
                        )}
                        {presupuesto.empresa?.cif_nif && (
                          <p className="text-slate-500 text-sm">CIF/NIF: {presupuesto.empresa.cif_nif}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Presupuesto</p>
                        <p className="font-mono text-slate-900">{presupuesto.numero}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {formatDate(presupuesto.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-500">Para:</p>
                      <p className="font-medium text-slate-900">{presupuesto.cliente?.nombre}</p>
                      {presupuesto.cliente?.empresa_cliente && (
                        <p className="text-slate-600">{presupuesto.cliente.empresa_cliente}</p>
                      )}
                      <p className="text-slate-600">{presupuesto.cliente?.email}</p>
                    </div>
                  </div>
                  
                  <div className="quote-items">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 text-slate-500 text-sm font-medium">Concepto</th>
                          <th className="text-right py-2 text-slate-500 text-sm font-medium">Cant.</th>
                          <th className="text-right py-2 text-slate-500 text-sm font-medium">Precio</th>
                          <th className="text-right py-2 text-slate-500 text-sm font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {presupuesto.items?.map((item, index) => (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-3">{item.descripcion}</td>
                            <td className="py-3 text-right">{item.cantidad}</td>
                            <td className="py-3 text-right">{formatCurrency(item.precio_unitario)}</td>
                            <td className="py-3 text-right font-medium">{formatCurrency(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="quote-footer">
                    <div className="flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Subtotal</span>
                          <span>{formatCurrency(presupuesto.subtotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">IVA (21%)</span>
                          <span>{formatCurrency(presupuesto.iva)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold pt-2 border-t">
                          <span>Total</span>
                          <span>{formatCurrency(presupuesto.total)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-slate-200 text-sm text-slate-500">
                      <p><strong>Validez:</strong> {presupuesto.validez_dias} días</p>
                      <p><strong>Forma de pago:</strong> {presupuesto.forma_pago}</p>
                      {presupuesto.notas && <p className="mt-2"><strong>Notas:</strong> {presupuesto.notas}</p>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Public Link */}
            {presupuesto.estado !== 'borrador' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Enlace Público
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm font-mono break-all text-slate-600">
                    {publicUrl}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mt-3"
                    onClick={() => window.open(publicUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir enlace
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Tracking */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Tracking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Vistas</span>
                  <span className="font-medium">{presupuesto.vistas || 0}</span>
                </div>
                {presupuesto.enviado_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Enviado</span>
                    <span className="text-sm">{formatDateTime(presupuesto.enviado_at)}</span>
                  </div>
                )}
                {presupuesto.visto_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Primera vista</span>
                    <span className="text-sm">{formatDateTime(presupuesto.visto_at)}</span>
                  </div>
                )}
                {presupuesto.respondido_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Respondido</span>
                    <span className="text-sm">{formatDateTime(presupuesto.respondido_at)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Comment */}
            {presupuesto.comentario_cliente && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Comentario del Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 italic">"{presupuesto.comentario_cliente}"</p>
                </CardContent>
              </Card>
            )}

            {/* Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Historial de Eventos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {presupuesto.eventos?.map((evento, index) => (
                    <div key={evento.id || index} className="flex gap-3">
                      <div className="mt-0.5">{getEventIcon(evento.tipo)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900">{evento.descripcion}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {formatDateTime(evento.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
